import { useEffect } from 'react';
import posthog from 'posthog-js';
import type { CaptureResult } from 'posthog-js';

import { selectUser } from 'store';

import { LINK_TO_POSTHOG } from 'constants/externalLinks';
import { useAppSelector } from 'hooks';

import { redactSensitiveUrlParams } from 'utils/datadog';

// The URL properties PostHog records on events and on the person. Messages links carry the AI
// chat's thread id, and chat ids count as PHI (LMD-1821); the verify-identity link carries a
// single-use session token in its fragment (LMD-2273). The same redaction Datadog applies
// (utils/datadog) runs on each of them before an event leaves the browser.
//
// `$session_entry_url` is the one this list used to miss. PostHog records the session's landing
// URL once and then attaches it to every event in that session, so redacting `$current_url`
// alone still shipped the landing URL — token and all — on everything that followed.
const URL_PROPERTIES = [
  '$current_url',
  '$referrer',
  '$initial_current_url',
  '$initial_referrer',
  '$session_entry_url'
];

// A fixed list only ever covers the properties PostHog has today, and the gap above was exactly a
// property nobody had added to it. Anything whose name ends in `url`, `referrer` or `pathname`
// gets the same treatment, so the next one is covered before somebody notices it. It is a test on
// the property's *name*: a free-text property is not a URL and is left alone.
const URL_PROPERTY_PATTERN = /(?:url|referrer|pathname)$/i;

const isUrlProperty = (key: string) =>
  URL_PROPERTIES.includes(key) || URL_PROPERTY_PATTERN.test(key);

const redactUrls = (properties: Record<string, unknown> | undefined) => {
  if (!properties) return;

  Object.keys(properties).forEach((key) => {
    const value = properties[key];

    if (isUrlProperty(key) && typeof value === 'string') {
      properties[key] = redactSensitiveUrlParams(value);
    }
  });
};

export const redactCaptureUrls = (capture: CaptureResult | null): CaptureResult | null => {
  if (!capture) return capture;

  redactUrls(capture.properties);
  redactUrls(capture.$set);
  redactUrls(capture.$set_once);

  return capture;
};

export const useAppAnalytics = () => {
  const { accessToken } = useAppSelector(selectUser);

  useEffect(() => {
    if (!accessToken && import.meta.env.VITE_ENV !== 'local') {
      posthog.init(import.meta.env.VITE_POSTHOG_API_KEY || '', {
        api_host: LINK_TO_POSTHOG,
        before_send: redactCaptureUrls,
        // Drops the fragment from `$current_url`, `$initial_current_url`, `$session_entry_url`,
        // autocapture `href`s, heatmap URLs and the session-replay rrweb meta. The last three
        // `before_send` above cannot reach: there the URL is nested inside a payload instead of
        // sitting on a property of its own. Off by default in posthog-js 1.425 and only on by
        // default from `defaults: '2026-06-25'`, which we do not set. Safe here because the
        // portal routes on paths (BrowserRouter) — no hash carries anything analytical.
        disable_capture_url_hashes: true,
        enable_heatmaps: true
      });
      posthog.register({ application: 'care_patient_portal' });
    }
  }, []);
};
